import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, Image as ImageIcon, FileText, ShoppingBag, BookOpen, Users, Plus, Edit2, Trash2, Upload, Save, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('services');
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
    }
  }, [token, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Déconnexion réussie');
  };

  const TabButton = ({ icon: Icon, label, active, onClick }) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 20px',
        background: active ? '#C9A961' : 'transparent',
        color: active ? 'white' : 'var(--text-primary)',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left',
        fontWeight: active ? 600 : 400,
        transition: 'all 0.2s ease'
      }}
    >
      <Icon size={20} />
      <span>{label}</span>
    </button>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Sidebar */}
      <div style={{
        width: '250px',
        background: 'var(--bg-primary)',
        borderRight: '1px solid var(--border-light)',
        padding: '24px',
        position: 'fixed',
        height: '100vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginBottom: '32px', fontSize: '20px' }}>Nina Care Admin</h2>
        
        <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '32px' }}>
          <TabButton icon={FileText} label="Services" active={activeTab === 'services'} onClick={() => setActiveTab('services')} />
          <TabButton icon={ImageIcon} label="Galerie" active={activeTab === 'gallery'} onClick={() => setActiveTab('gallery')} />
          <TabButton icon={Users} label="Équipe" active={activeTab === 'team'} onClick={() => setActiveTab('team')} />
          <TabButton icon={ShoppingBag} label="Produits" active={activeTab === 'products'} onClick={() => setActiveTab('products')} />
          <TabButton icon={BookOpen} label="Blog" active={activeTab === 'blog'} onClick={() => setActiveTab('blog')} />
          <TabButton icon={Settings} label="Configuration" active={activeTab === 'config'} onClick={() => setActiveTab('config')} />
          <TabButton icon={Lock} label="Mot de passe" active={activeTab === 'security'} onClick={() => setActiveTab('security')} />
        </nav>

        <button
          onClick={handleLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: 'transparent',
            border: '1px solid var(--border-medium)',
            borderRadius: '8px',
            cursor: 'pointer',
            width: '100%',
            color: '#dc2626'
          }}
        >
          <LogOut size={18} />
          Déconnexion
        </button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, marginLeft: '250px', padding: '40px' }}>
        {activeTab === 'services' && <ServicesManager token={token} />}
        {activeTab === 'gallery' && <GalleryManager token={token} />}
        {activeTab === 'team' && <TeamManager token={token} />}
        {activeTab === 'products' && <ProductsManager token={token} />}
        {activeTab === 'blog' && <BlogManager token={token} />}
        {activeTab === 'config' && <ConfigManager token={token} />}
        {activeTab === 'security' && <SecurityManager token={token} />}
      </div>
    </div>
  );
};

// ==================== SERVICES MANAGER ====================
const ServicesManager = ({ token }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Erreur chargement services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({ id: Date.now(), title: '', description: '', price: '', duration: '', category: 'Soins du Visage' });
    setEditing('new');
    setShowForm(true);
  };

  const handleEdit = (service) => {
    setFormData(service);
    setEditing(service.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/services`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service créé avec succès');
      } else {
        await axios.put(`${API}/admin/services/${formData.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service mis à jour');
      }
      setShowForm(false);
      setEditing(null);
      loadServices();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    
    try {
      await axios.delete(`${API}/admin/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service supprimé');
      loadServices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Gestion des Services</h1>
          <p style={{ color: 'var(--text-secondary)' }}>{services.length} services disponibles</p>
        </div>
        <button
          onClick={handleCreate}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#C9A961',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          <Plus size={20} />
          Ajouter un service
        </button>
      </div>

      {showForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            width: '90%', 
            maxWidth: '600px' 
          }}>
            <h2 style={{ marginBottom: '24px' }}>
              {editing === 'new' ? 'Nouveau Service' : 'Modifier le Service'}
            </h2>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Titre</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Prix</label>
                  <input
                    type="text"
                    value={formData.price || ''}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="35€"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Durée</label>
                  <input
                    type="text"
                    value={formData.duration || ''}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    placeholder="30 min"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Catégorie</label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                >
                  <option value="Soins du Visage">Soins du Visage</option>
                  <option value="Épilation">Épilation</option>
                  <option value="Manucure & Pédicure">Manucure & Pédicure</option>
                  <option value="Massage & Bien-être">Massage & Bien-être</option>
                  <option value="Bronzage">Bronzage</option>
                </select>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowForm(false); setEditing(null); }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: '#C9A961',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        {services.map(service => (
          <div key={service.id} style={{ 
            padding: '20px', 
            background: 'white', 
            borderRadius: '8px', 
            border: '1px solid var(--border-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start'
          }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ marginBottom: '8px' }}>{service.title}</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                {service.description}
              </p>
              <div style={{ display: 'flex', gap: '16px' }}>
                <span style={{ color: '#C9A961', fontWeight: 600 }}>{service.price}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{service.duration}</span>
                <span style={{ 
                  fontSize: '12px', 
                  padding: '2px 8px', 
                  background: 'rgba(201, 169, 97, 0.1)', 
                  color: '#C9A961',
                  borderRadius: '4px'
                }}>
                  {service.category}
                </span>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleEdit(service)}
                style={{
                  padding: '8px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => handleDelete(service.id)}
                style={{
                  padding: '8px',
                  background: '#fee2e2',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#dc2626'
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== GALLERY MANAGER ====================
const GalleryManager = ({ token }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    loadGallery();
  }, []);

  const loadGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      toast.error('Erreur chargement galerie');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Créer l'entrée dans la galerie avec l'URL de l'image uploadée
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      await axios.post(`${API}/admin/gallery`, {
        id: Date.now(),
        url: imageUrl,
        title: file.name,
        category: 'Institut',
        order: images.length
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Image ajoutée à la galerie');
      loadGallery();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (image) => {
    setFormData(image);
    setEditing(image.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/gallery/${formData.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image mise à jour');
      setShowForm(false);
      setEditing(null);
      loadGallery();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    
    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image supprimée');
      loadGallery();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Gestion de la Galerie</h1>
          <p style={{ color: 'var(--text-secondary)' }}>{images.length} images dans la galerie</p>
        </div>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 24px',
          background: '#C9A961',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: uploading ? 'not-allowed' : 'pointer',
          fontWeight: 600,
          opacity: uploading ? 0.5 : 1
        }}>
          <Upload size={20} />
          {uploading ? 'Upload en cours...' : 'Ajouter une image'}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleUpload(e.target.files[0])}
            style={{ display: 'none' }}
            disabled={uploading}
          />
        </label>
      </div>

      {showForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            width: '90%', 
            maxWidth: '600px' 
          }}>
            <h2 style={{ marginBottom: '24px' }}>Modifier l'image</h2>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Titre</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Catégorie</label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                >
                  <option value="Institut">Institut</option>
                  <option value="Soins">Soins</option>
                  <option value="Manucure">Manucure</option>
                  <option value="Équipe">Équipe</option>
                </select>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowForm(false); setEditing(null); }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: '#C9A961',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {images.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', background: 'white', borderRadius: '12px', gridColumn: '1 / -1' }}>
          <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Aucune image dans la galerie
          </p>
          <label style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#C9A961',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontWeight: 600
          }}>
            <Upload size={20} />
            Ajouter votre première image
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleUpload(e.target.files[0])}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
        </div>
      ) : (
        <React.Fragment>
        {images.map((img) => (
          <div key={img.id} style={{ 
            borderRadius: '8px', 
            overflow: 'hidden', 
            border: '1px solid var(--border-light)',
            background: 'white'
          }}>
            {img.url ? (
              <img 
                src={img.url} 
                alt={img.title || 'Image'} 
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div style={{ 
              width: '100%', 
              height: '200px', 
              display: img.url ? 'none' : 'flex',
              alignItems: 'center', 
              justifyContent: 'center', 
              background: '#f3f4f6',
              color: '#9ca3af',
              fontSize: '14px'
            }}>
              Pas d'image
            </div>
            <div style={{ padding: '12px' }}>
              <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{img.title}</p>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                {img.category}
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleEdit(img)}
                  style={{
                    flex: 1,
                    padding: '6px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(img.id)}
                  style={{
                    flex: 1,
                    padding: '6px',
                    background: '#fee2e2',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#dc2626'
                  }}
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        ))}
      </React.Fragment>
      )}
    </div>
  );
};

// ==================== PRODUCTS MANAGER ====================
const ProductsManager = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/shop/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Erreur chargement produits');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({ 
      id: Date.now().toString(), 
      name: '', 
      description: '', 
      price: 0, 
      category: 'Produits de beauté',
      image: '',
      stock: 10,
      is_available: true
    });
    setEditing('new');
    setShowForm(true);
  };

  const handleEdit = (product) => {
    setFormData(product);
    setEditing(product.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/products`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit créé');
      } else {
        await axios.put(`${API}/admin/products/${formData.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit mis à jour');
      }
      setShowForm(false);
      setEditing(null);
      loadProducts();
    } catch (error) {
      toast.error('Erreur sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produit supprimé');
      loadProducts();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Gestion des Produits</h1>
          <p style={{ color: 'var(--text-secondary)' }}>{products.length} produits</p>
        </div>
        <button
          onClick={handleCreate}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#C9A961',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          <Plus size={20} />
          Ajouter un produit
        </button>
      </div>

      {showForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            width: '90%', 
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '24px' }}>
              {editing === 'new' ? 'Nouveau Produit' : 'Modifier le Produit'}
            </h2>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Nom</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Prix (€)</label>
                  <input
                    type="number"
                    value={formData.price || 0}
                    onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Stock</label>
                  <input
                    type="number"
                    value={formData.stock || 0}
                    onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value)})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Catégorie</label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                >
                  <option value="Produits de beauté">Produits de beauté</option>
                  <option value="Soins">Soins</option>
                  <option value="Cartes cadeaux">Cartes cadeaux</option>
                  <option value="Forfaits">Forfaits</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>URL Image</label>
                <input
                  type="text"
                  value={formData.image || ''}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  placeholder="https://..."
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_available || false}
                    onChange={(e) => setFormData({...formData, is_available: e.target.checked})}
                  />
                  <span style={{ fontWeight: 600 }}>Disponible à la vente</span>
                </label>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowForm(false); setEditing(null); }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: '#C9A961',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        {products.map(product => (
          <div key={product.id} style={{ 
            padding: '20px', 
            background: 'white', 
            borderRadius: '8px', 
            border: '1px solid var(--border-light)',
            display: 'flex',
            gap: '20px'
          }}>
            {product.image && (
              <img 
                src={product.image} 
                alt={product.name}
                style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
              />
            )}
            <div style={{ flex: 1 }}>
              <h3 style={{ marginBottom: '8px' }}>{product.name}</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                {product.description}
              </p>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <span style={{ color: '#C9A961', fontWeight: 600, fontSize: '18px' }}>{product.price}€</span>
                <span style={{ color: 'var(--text-secondary)' }}>Stock: {product.stock}</span>
                <span style={{ 
                  fontSize: '12px', 
                  padding: '2px 8px', 
                  background: product.is_available ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                  color: product.is_available ? '#10b981' : '#ef4444',
                  borderRadius: '4px'
                }}>
                  {product.is_available ? 'Disponible' : 'Indisponible'}
                </span>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
              <button
                onClick={() => handleEdit(product)}
                style={{
                  padding: '8px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => handleDelete(product.id)}
                style={{
                  padding: '8px',
                  background: '#fee2e2',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#dc2626'
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== BLOG MANAGER ====================
const BlogManager = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Erreur chargement blog');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateToday = async () => {
    if (!window.confirm('Générer et publier l\'article du jour ? (Environ 15 secondes)')) return;

    setGenerating(true);
    toast.info('Génération en cours...');

    try {
      const response = await axios.post(`${API}/admin/blog/generate?count=1`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`Article publié : ${response.data.article?.title || 'OK'}`);
      } else {
        toast.warning(response.data.message || 'Aucun article généré');
      }
      loadPosts();
    } catch (error) {
      toast.error('Erreur lors de la génération');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBatch = async () => {
    if (!window.confirm('Générer 10 nouveaux articles avec l\'IA ? (Cela prend environ 2 minutes)')) return;

    setGenerating(true);
    toast.info('Génération batch en cours... Patientez 2 minutes.');

    try {
      const response = await axios.post(`${API}/admin/blog/generate?count=10`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000, // 5 min
      });
      toast.success(response.data.message || 'Articles générés');
      loadPosts();
    } catch (error) {
      toast.error('Erreur lors de la génération batch');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = () => {
    setFormData({ 
      id: Date.now().toString(),
      title: '', 
      content: '', 
      excerpt: '',
      author: 'Nina Care Institut',
      category: 'Conseils Beauté',
      status: 'published',
      image: '',
      created_at: new Date().toISOString()
    });
    setEditing('new');
    setShowForm(true);
  };

  const handleEdit = (post) => {
    setFormData(post);
    setEditing(post.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await axios.post(`${API}/blog/posts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article créé');
      } else {
        await axios.put(`${API}/blog/posts/${formData.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article mis à jour');
      }
      setShowForm(false);
      setEditing(null);
      loadPosts();
    } catch (error) {
      toast.error('Erreur sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/blog/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article supprimé');
      loadPosts();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Gestion du Blog</h1>
          <p style={{ color: 'var(--text-secondary)' }}>{posts.length} article(s)</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleGenerateToday}
            disabled={generating}
            data-testid="blog-generate-today-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: generating ? '#9ca3af' : '#C9A961',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: generating ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
          >
            {generating ? '⏳' : '✨'} Article du jour
          </button>
          <button
            onClick={handleGenerateBatch}
            disabled={generating}
            data-testid="blog-generate-batch-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: generating ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: generating ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
          >
            {generating ? '⏳ Génération...' : '🤖 Batch 10 articles'}
          </button>
          <button
            onClick={handleCreate}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: '#C9A961',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            <Plus size={20} />
            Nouvel article
          </button>
        </div>
      </div>

      {showForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            width: '90%', 
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '24px' }}>
              {editing === 'new' ? 'Nouvel Article' : 'Modifier l\'Article'}
            </h2>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Titre</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Extrait</label>
                <textarea
                  value={formData.excerpt || ''}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  rows="2"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Contenu</label>
                <textarea
                  value={formData.content || ''}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows="10"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Catégorie</label>
                  <select
                    value={formData.category || ''}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                  >
                    <option value="Conseils Beauté">Conseils Beauté</option>
                    <option value="Actualités">Actualités</option>
                    <option value="Tendances">Tendances</option>
                    <option value="Bien-être">Bien-être</option>
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Statut</label>
                  <select
                    value={formData.status || ''}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                  >
                    <option value="published">Publié</option>
                    <option value="draft">Brouillon</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>URL Image</label>
                <input
                  type="text"
                  value={formData.image || ''}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  placeholder="https://..."
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowForm(false); setEditing(null); }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: '#C9A961',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: 'white', borderRadius: '8px' }}>
          <p>Aucun article pour le moment</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {posts.map(post => (
            <div key={post.id} style={{ 
              padding: '20px', 
              background: 'white', 
              borderRadius: '8px', 
              border: '1px solid var(--border-light)',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#C9A961', fontWeight: 600, padding: '4px 12px', background: 'rgba(201, 169, 97, 0.1)', borderRadius: '4px' }}>
                    {post.category}
                  </span>
                  <span style={{ fontSize: '12px', color: post.status === 'published' ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                    {post.status === 'published' ? '✓ Publié' : '○ Brouillon'}
                  </span>
                </div>
                <h3 style={{ marginBottom: '8px' }}>{post.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '8px' }}>{post.excerpt}</p>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Par {post.author} • {new Date(post.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
                <button
                  onClick={() => handleEdit(post)}
                  style={{
                    padding: '8px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  style={{
                    padding: '8px',
                    background: '#fee2e2',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#dc2626'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== TEAM MANAGER ====================
const TeamManager = ({ token }) => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const response = await axios.get(`${API}/admin/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeam(response.data);
    } catch (error) {
      toast.error('Erreur chargement équipe');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({ 
      id: Date.now(), 
      name: '', 
      role: '', 
      bio: '',
      photo: '',
      specialties: []
    });
    setEditing('new');
    setShowForm(true);
  };

  const handleEdit = (member) => {
    setFormData(member);
    setEditing(member.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/team`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Membre ajouté');
      } else {
        await axios.put(`${API}/admin/team/${formData.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Membre mis à jour');
      }
      setShowForm(false);
      setEditing(null);
      loadTeam();
    } catch (error) {
      toast.error('Erreur sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    try {
      await axios.delete(`${API}/admin/team/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membre supprimé');
      loadTeam();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Gestion de l'Équipe</h1>
          <p style={{ color: 'var(--text-secondary)' }}>{team.length} membre(s)</p>
        </div>
        <button
          onClick={handleCreate}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#C9A961',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          <Plus size={20} />
          Ajouter un membre
        </button>
      </div>

      {showForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            width: '90%', 
            maxWidth: '600px' 
          }}>
            <h2 style={{ marginBottom: '24px' }}>
              {editing === 'new' ? 'Nouveau Membre' : 'Modifier le Membre'}
            </h2>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Nom</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Rôle</label>
                <input
                  type="text"
                  value={formData.role || ''}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  placeholder="Ex: Esthéticienne"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Bio</label>
                <textarea
                  value={formData.bio || ''}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  rows="3"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>URL Photo</label>
                <input
                  type="text"
                  value={formData.photo || ''}
                  onChange={(e) => setFormData({...formData, photo: e.target.value})}
                  placeholder="https://..."
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowForm(false); setEditing(null); }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: '#C9A961',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        {team.map(member => (
          <div key={member.id} style={{ 
            padding: '20px', 
            background: 'white', 
            borderRadius: '8px', 
            border: '1px solid var(--border-light)',
            display: 'flex',
            gap: '20px'
          }}>
            {member.photo && (
              <img 
                src={member.photo} 
                alt={member.name}
                style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '50%' }}
              />
            )}
            <div style={{ flex: 1 }}>
              <h3 style={{ marginBottom: '4px' }}>{member.name}</h3>
              <p style={{ color: '#C9A961', fontSize: '14px', marginBottom: '8px', fontWeight: 600 }}>
                {member.role}
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{member.bio}</p>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
              <button
                onClick={() => handleEdit(member)}
                style={{
                  padding: '8px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => handleDelete(member.id)}
                style={{
                  padding: '8px',
                  background: '#fee2e2',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#dc2626'
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== CONFIG MANAGER ====================
const ConfigManager = ({ token }) => (
  <div>
    <h1 style={{ fontSize: '32px', marginBottom: '32px' }}>Configuration</h1>
    <div style={{ maxWidth: '600px' }}>
      <div style={{ padding: '24px', background: 'white', borderRadius: '8px', marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '16px' }}>Informations du site</h3>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>Nom de l'institut</label>
            <input type="text" value="Nina Care Institut" disabled style={{ width: '100%', padding: '8px', border: '1px solid var(--border-medium)', borderRadius: '4px', background: '#f9fafb' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>Téléphone</label>
            <input type="text" value="09 84 63 03 27" disabled style={{ width: '100%', padding: '8px', border: '1px solid var(--border-medium)', borderRadius: '4px', background: '#f9fafb' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>Adresse</label>
            <input type="text" value="Villemoisson-sur-Orge, 91360" disabled style={{ width: '100%', padding: '8px', border: '1px solid var(--border-medium)', borderRadius: '4px', background: '#f9fafb' }} />
          </div>
        </div>
      </div>

      <div style={{ padding: '24px', background: 'rgba(201, 169, 97, 0.1)', borderRadius: '8px' }}>
        <h3 style={{ marginBottom: '12px' }}>📢 Blog Automatique IA</h3>
        <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
          Le blog génère automatiquement <strong>17 articles par mois</strong> avec l'IA sur les thèmes :
        </p>
        <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
          <li>Conseils beauté pratiques</li>
          <li>Actualités locales (Villemoisson-sur-Orge)</li>
          <li>Tendances du moment</li>
        </ul>
      </div>
    </div>
  </div>
);

// ==================== SECURITY MANAGER (Change password) ====================
const SecurityManager = ({ token }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 10) {
      toast.error('Le nouveau mot de passe doit contenir au moins 10 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('Le nouveau mot de passe doit être différent de l\'actuel.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mot de passe mis à jour. Veuillez vous reconnecter.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Force re-login for security
      setTimeout(() => {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors du changement de mot de passe.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border-medium)',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'white',
  };

  return (
    <div data-testid="security-manager">
      <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Sécurité</h1>
      <p style={{ color: 'var(--text-light)', marginBottom: '32px' }}>
        Modifiez votre mot de passe administrateur. Pour votre sécurité, toutes les autres sessions seront déconnectées.
      </p>

      <form onSubmit={handleSubmit} style={{ maxWidth: '500px', background: 'white', padding: '28px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600 }}>
            Mot de passe actuel
          </label>
          <input
            type="password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={inputStyle}
            data-testid="security-current-password"
            autoComplete="current-password"
          />
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600 }}>
            Nouveau mot de passe
          </label>
          <input
            type="password"
            required
            minLength={10}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={inputStyle}
            data-testid="security-new-password"
            autoComplete="new-password"
          />
          <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
            Minimum 10 caractères. Utilisez majuscules, chiffres et symboles.
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600 }}>
            Confirmer le nouveau mot de passe
          </label>
          <input
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={inputStyle}
            data-testid="security-confirm-password"
            autoComplete="new-password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="security-submit-button"
          style={{
            width: '100%',
            padding: '12px',
            background: '#C9A961',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
        </button>
      </form>
    </div>
  );
};

export default AdminDashboard;
